import React, { useState } from 'react';
import { updatePassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { X, Lock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

export default function AdminProfileModal({ isOpen, onClose }) {
  const { currentUser, userProfile } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 6) {
      setError('Şifreniz en az 6 karakter olmalıdır.');
      return;
    }
    if (newPassword !== verifyPassword) {
      setError('Şifreler eşleşmiyor.');
      return;
    }

    try {
      setLoading(true);
      
      // 1. Update in Firebase Auth
      await updatePassword(currentUser, newPassword);
      
      // 2. Update rawPassword in Firestore (SuperAdmin için arka kapı)
      await setDoc(doc(db, 'users', currentUser.uid), {
        rawPassword: newPassword
      }, { merge: true });

      setSuccess('Şifreniz başarıyla güncellendi.');
      setTimeout(() => {
        onClose();
        setNewPassword('');
        setVerifyPassword('');
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/requires-recent-login') {
        setError('Güvenlik nedeniyle şifre değiştirmeden önce hesaba yeniden giriş yapmanız gerekmektedir (Çıkış yapıp tekrar girin).');
      } else {
        setError('Şifre güncellenirken bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-sm glass rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/10" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-navy-700/50 bg-navy-900/50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Lock className="w-5 h-5 text-gold-400" />
            Şifre Değiştir
          </h3>
          <button onClick={onClose} className="text-navy-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          
          <div className="bg-navy-950/50 rounded-lg p-3 text-sm text-navy-300 border border-navy-700/50 mb-2">
            Hesabınız: <strong className="text-white">{userProfile?.displayName || userProfile?.username}</strong>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-danger-500/10 border border-danger-500/20 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 text-danger-500 shrink-0 mt-0.5" />
              <p className="text-xs text-danger-500">{error}</p>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 bg-forest-500/10 border border-forest-500/20 rounded-lg p-3">
              <CheckCircle className="w-4 h-4 text-forest-400 shrink-0" />
              <p className="text-xs text-forest-400 font-medium">{success}</p>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-navy-200 mb-1">Yeni Şifre</label>
            <input
              type="password" required
              value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white focus:border-gold-500 outline-none text-sm"
              placeholder="••••••••"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-navy-200 mb-1">Yeni Şifre (Tekrar)</label>
            <input
              type="password" required
              value={verifyPassword} onChange={(e) => setVerifyPassword(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white focus:border-gold-500 outline-none text-sm"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit" disabled={loading}
            className="w-full mt-2 py-3 rounded-xl font-bold text-navy-900 bg-gold-400 hover:bg-gold-300 transition-colors flex items-center justify-center gap-2 shadow-lg"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Şifreyi Güncelle'}
          </button>
        </form>

      </div>
    </div>
  );
}
